import _ from 'lodash'
import { Mappings } from 'shared-libs/models/types/storyboard/storyboard-plan-model'
import { resolveMappings } from 'shared-libs/models/types/storyboard/storyboard-utils'
import { EventFieldResponse } from './ui2-event-field-props'

/**
 * The execution paths the server is asked to read back — the `source` of every mapping that has
 * one. Derived rather than declared separately so a schema author can't route a value they never
 * asked the server to return.
 */
export const eventReturnPaths = (valueMappings?: Mappings): string[] =>
  _.uniq(
    _.compact(_.map(valueMappings, (mapping) => _.trim(mapping?.source ?? '')))
  )

/**
 * Builds the context mappings resolve against.
 *
 * `resolvedValues` comes back flat, keyed by the literal path string, so it is re-nested here: a
 * mapping's `source` is a path, and `_.get` has to be able to walk it. The flat map is kept
 * alongside under `resolvedValues` for a mapping that would rather index it directly.
 */
export const eventMappingContext = (response: EventFieldResponse) => {
  const nested = {}
  _.forEach(response?.resolvedValues ?? {}, (value, path) => {
    _.set(nested, path, value)
  })
  return {
    ...nested,
    resolvedValues: response?.resolvedValues ?? {},
    eventResponse: {
      success: response?.success,
      errorMsg: response?.errorMsg,
      dryRun: response?.dryRun,
      resolvedValues: response?.resolvedValues ?? {},
    },
  }
}

/**
 * Resolves `valueMappings` against a fired event's response into the form writes they imply. Each
 * entry's `destination` is an absolute form path, so callers write them one at a time through the
 * renderer's `overridePath` channel rather than merging them into the field's own value.
 */
export const resolveEventMappings = (
  valueMappings: Mappings | undefined,
  frames: any,
  response: EventFieldResponse
): { destination: string; value: any }[] => {
  if (_.isEmpty(valueMappings)) {
    return []
  }
  return resolveMappings(valueMappings, frames, eventMappingContext(response))
    .filter((mapping): mapping is { destination: string; value: any } =>
      !_.isEmpty(mapping.destination))
}
